'use client'
import { captureFatalException } from '@src/quince-site-common/src/utils/sentryUtil'
import React, { PropsWithChildren } from 'react'
import { ErrorInfo } from 'react-dom/client'

interface ErrorBoundaryProps {
  onReset?: (...args: any[]) => void
  onError?: (error: any, componentStack: any) => void
  fallbackRender?: (props: any) => JSX.Element
  FallbackComponent?: (props: any) => JSX.Element | null
  fallback?: React.ReactElement
  message?: string
}

interface ErrorBoundaryState {
  error?: any
  info?: ErrorInfo | null
}

const initialState: ErrorBoundaryState = { error: null, info: null }

class ErrorBoundary extends React.Component<PropsWithChildren<ErrorBoundaryProps>, ErrorBoundaryState> {
  override state = initialState

  resetErrorBoundary = (...args: any[]) => {
    if (this.props.onReset) this.props.onReset.apply(null, args)
    this.setState(initialState)
  }

  override componentDidCatch(error: any, info?: { componentStack?: any }) {
    if (this.props.onError) this.props.onError(error, info?.componentStack)
    captureFatalException(error, info)
    this.setState({ error, info })
  }

  override render() {
    const { error, info } = this.state
    const { fallbackRender, FallbackComponent, fallback, message } = this.props

    if (error !== null) {
      const props = {
        componentStack: info?.componentStack,
        error,
        resetErrorBoundary: this.resetErrorBoundary,
      }
      if (React.isValidElement(fallback)) {
        return fallback
      } else if (typeof fallbackRender === 'function') {
        return fallbackRender(props)
      } else if (FallbackComponent) {
        return <FallbackComponent {...props} message={message} />
      } else {
        // @i18n-ignore
        throw new Error('react-error-boundary requires either a fallback, fallbackRender, or FallbackComponent prop')
      }
    }

    return this.props.children || <div></div>
  }
}

export default ErrorBoundary
